import uuid from 'node-uuid'
import {apiOption, apiResponse, errorResponse, only} from '../core/fetchz'
import loading from '../core/redux/loading'
import config from '../config'

const NAMESPACE = 'User'

export const LOGGED_IN = NAMESPACE + '/LoggedIn'
export const LOGGED_OUT = NAMESPACE + '/LoggedOut'
export const AUTHENTICATED_USER_LOADED = NAMESPACE + '/AuthenticatedUserLoaded'
export const AUTHENTICATED_USER_DISCARDED = NAMESPACE + '/AuthenticatedUserDiscarded'
export const AUTHENTICATED_USER_CONFIGS_LOADED = NAMESPACE + '/AuthenticatedUserConfigsLoaded'
export const AUTHENTICATED_USER_CONFIGS_RESET = NAMESPACE + '/AuthenticatedUserConfigsReset'
export const AUTHENTICATED_USER_CONFIGS_SENT = NAMESPACE + '/AuthenticatedUserConfigsSent'
export const UPDATE_USER_CMD_SENT = NAMESPACE + '/UpdateUserCmdSent'
export const ACTIVE_USER_CMD_SENT = NAMESPACE + '/ActiveUserCmdSent'
export const RESET_PASSWORD_DEMAND_SENT = NAMESPACE + '/ResetPasswordDemandSent'
export const RESET_PASSWORD_CMD_SENT = NAMESPACE + '/ResetPasswordCmdSent'
export const RESET_LOGIN_DEMAND_SENT = NAMESPACE + '/ResetLoginDemandSent'
export const RESET_LOGIN_CMD_SENT = NAMESPACE + '/ResetLoginCmdSent'
// form action
export const CREDENTIAL_UPDATED = NAMESPACE + '/CredentialUpdated'
export const AUTHENTICATED_USER_CONFIGS_UPDATED = NAMESPACE + '/AuthenticatedUserConfigsUpdated'
export const UPDATE_USER_CMD_UPDATED = NAMESPACE + '/UpdateUserCmdUpdated'
export const ACTIVE_USER_CMD_UPDATED = NAMESPACE + '/ActiveUserCmdUpdated'
export const RESET_PASSWORD_DEMAND_UPDATED = NAMESPACE + '/ResetPasswordDemandUpdated'
export const RESET_PASSWORD_CMD_UPDATED = NAMESPACE + '/ResetPasswordCmdUpdated'
export const RESET_LOGIN_DEMAND_UPDATED = NAMESPACE + '/ResetLoginDemandUpdated'

export function loadAuthenticatedUser() {
    return fetch(config.api.url + '/users/auth', apiOption())
    .then(apiResponse)
    .then(({json}) => {
        return {
            type: AUTHENTICATED_USER_LOADED,
            user: json
        }
    })
}

export function loadAuthenticatedUserConfigs() {
    return loading((state) => {
        const {authenticated} = state.user

        return fetch(config.api.url + '/users/' + authenticated.uid + '/configs', apiOption())
        .then(apiResponse)
        .then(({json}) => {
            return {
                type: AUTHENTICATED_USER_CONFIGS_LOADED,
                userConfigs: json
            }
        })
    }, AUTHENTICATED_USER_CONFIGS_LOADED)
}

export function sendAuthenticatedUserConfigs() {
    return loading((state) => {
        const {authenticated, authenticatedConfigs} = state.user

        const option = {
            method: 'PATCH',
            body: authenticatedConfigs
        }

        return fetch(config.api.url + '/users/' + authenticated.uid + '/configs', apiOption(option))
        .then(apiResponse)
        .then(() => {
            return {
                type: AUTHENTICATED_USER_CONFIGS_SENT
            }
        })
    }, AUTHENTICATED_USER_CONFIGS_SENT)
}

export function login() {
    return (state) => {
        const {credential} = state.user
        const {login, password} = credential

        const option = {
            body: {
                login: login.trim(),
                password: password
            }
        }

        return fetch(config.api.url + '/login', apiOption(option))
        .then(errorResponse)
        .then(() => {
            return {
                type: LOGGED_IN
            }
        })
    }
}

export function logout() {
    return fetch(config.api.url + '/logout', apiOption())
    .then(errorResponse)
    .then(() => {
        return {
            type: LOGGED_OUT
        }
    })
}

export function sendActiveUserCmd() {
    return (state) => {
        const {activeUserCmd} = state.user
        const {userId, confirmPassword, ...cmd} = activeUserCmd

        const option = {
            method: 'PATCH',
            body: {
                processUid: uuid.v4(),
                commandType: 'ActiveUser',
                ...cmd
            }
        }

        return fetch(config.api.event.url + '/users/' + userId, apiOption(option))
        .then(errorResponse)
        .then(() => {
            return {
                type: ACTIVE_USER_CMD_SENT
            }
        })
    }
}

export function sendResetPasswordDemand(demand) {
    return (state) => {
        const {resetPasswordDemand} = state.user

        demand = {
            ...resetPasswordDemand,
            ...demand
        }

        const option = {
            body: demand
        }

        return fetch(config.api.url + '/resetPasswordDemand', apiOption(option))
        .then(errorResponse)
        .then(() => {
            return {
                type: RESET_PASSWORD_DEMAND_SENT
            }
        }).catch(only(404, (error) => {
            return Promise.reject({...error, type: 'UserNotFound'})
        }))
    }
}

export function sendResetPasswordCmd() {
    return (state) => {
        const {resetPasswordCmd} = state.user
        const {userId, confirmPassword, ...cmd} = resetPasswordCmd

        const option = {
            method: 'PATCH',
            body: {
                processUid: uuid.v4(),
                commandType: 'ResetPassword',
                ...cmd
            }
        }

        return fetch(config.api.event.url + '/users/' + userId, apiOption(option))
        .then(errorResponse)
        .then(() => {
            return {
                type: RESET_PASSWORD_CMD_SENT
            }
        })
    }
}

export function sendUpdateUserCmd() {
    return loading((state) => {
        const {authenticated, updateUserCmd} = state.user
        
        const option = {
            method: 'PATCH',
            body: {
                processUid: uuid.v4(),
                commandType: 'UpdateUser',
                ...updateUserCmd
            }
        }

        return fetch(config.api.event.url + '/users/' + authenticated.uid, apiOption(option))
        .then(errorResponse)
        .then(() => {
            return {
                type: UPDATE_USER_CMD_SENT
            }
        })
    }, UPDATE_USER_CMD_SENT)
}

export function sendResetLoginDemand() {
    return (state) => {
        const {resetLoginDemand} = state.user

        const option = {
            body: resetLoginDemand
        }

        return fetch(config.api.url + '/resetLoginDemand', apiOption(option))
        .then(errorResponse)
        .then(() => {
            return {
                type: RESET_LOGIN_DEMAND_SENT
            }
        })
    }
}

export function sendResetLoginCmd(cmd) {
    const {userId, ...apiCmd} = cmd

    const option = {
        method: 'PATCH',
        body: {
            processUid: uuid.v4(),
            commandType: 'ResetLogin',
            ...apiCmd
        }
    }

    return loading(fetch(config.api.event.url + '/users/' + userId, apiOption(option))
    .then(errorResponse)
    .then(() => {
        return {
            type: RESET_LOGIN_CMD_SENT
        }
    }), RESET_LOGIN_CMD_SENT)
}

export function updateActiveUserCmd(cmd) {
    return {
        type: ACTIVE_USER_CMD_UPDATED,
        cmd: cmd
    }
}

export function updateResetPasswordCmd(cmd) {
    return {
        type: RESET_PASSWORD_CMD_UPDATED,
        cmd: cmd
    }
}

export function updateUpdateUserCmd(cmd) {
    return {
        type: UPDATE_USER_CMD_UPDATED,
        cmd: cmd
    }
}

export function updateResetLoginDemand(demand) {
    return {
        type: RESET_LOGIN_DEMAND_UPDATED,
        demand: demand
    }
}

export function resetAuthenticatedUserConfigs(userConfigs) {
    return {
        type: AUTHENTICATED_USER_CONFIGS_RESET,
        userConfigs: userConfigs
    }
}

export function discardAuthenticatedUser() {
    return {
        type: AUTHENTICATED_USER_DISCARDED
    }
}


// WEBPACK FOOTER //
// ./lib/user/actions.js